import React, { useMemo } from "react";

import { buildSparklinePaths } from "../../utils/chartTimeAxis";
import type { SparklinePoint } from "../../utils/chartTimeAxis";

interface TrendSparklineLine {
  points: SparklinePoint[];
  /** Ink for this line; it draws in currentColor. */
  className?: string;
  /** A literal colour for this line instead of a class, when it must match a chart's palette. */
  color?: string;
}

interface TrendSparklineProps {
  /** Drawn on one shared scale; the first line is the lead and the only one with a quantile band. */
  lines: TrendSparklineLine[];
  /** Trims each line to this many days either side of now, when that window holds data. */
  horizonDays?: number;
  /** Size, plus the ink for the now marker. */
  className?: string;
}

const VIEW_W = 300;
const VIEW_H = 70;

// Series as trends: history solid, forecast lighter, the lead's quantile band behind and a dotted mark at now; stretches to its box
const TrendSparkline: React.FC<TrendSparklineProps> = ({ lines, horizonDays, className = "" }) => {
  const paths = useMemo(
    () => buildSparklinePaths(lines.map((l) => l.points), VIEW_W, VIEW_H, { horizonDays }),
    [lines, horizonDays]
  );
  if (!paths) return null;

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none" aria-hidden className={className}>
      {paths.nowX !== null && (
        <line
          x1={paths.nowX}
          y1={2}
          x2={paths.nowX}
          y2={VIEW_H - 2}
          stroke="currentColor"
          strokeOpacity={0.6}
          strokeDasharray="2 3"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {/* Painted last-to-first, so the lead line sits on top of the others */}
      {paths.layers
        .map((layer, i) => (
          <g key={i} className={lines[i]?.className} style={lines[i]?.color ? { color: lines[i].color } : undefined}>
            {layer.band && <path d={layer.band} fill="currentColor" fillOpacity={0.18} />}
            {layer.past && (
              <path d={layer.past} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            )}
            {/* Lighter rather than dashed — dashes on a jagged real series read as dots */}
            {layer.future && (
              <path
                d={layer.future}
                fill="none"
                stroke="currentColor"
                strokeOpacity={0.55}
                strokeWidth={1.75}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </g>
        ))
        .reverse()}
    </svg>
  );
};

export default TrendSparkline;
